import { useState, useEffect, useRef } from 'react';
import {
  FolderOpen, Plus, Image, X, ChevronLeft, ChevronRight,
  Edit2, Trash2, Upload, Search, Tag, Maximize2,
  Grid, List, MoreVertical, GripVertical, Package
} from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';

export default function SalesTool({ companyId }) {
  const { colors } = useTheme();
  const { showSuccess, showError, showConfirm } = useModal();
  const [categories, setCategories] = useState([]);
  const [slides, setSlides] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'presentation'
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddSlide, setShowAddSlide] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSlide, setEditingSlide] = useState(null);
  const [skus, setSkus] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load categories
      const { data: catData, error: catError } = await supabaseData
        .from('sales_categories')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('sort_order');

      if (catError) throw catError;
      setCategories(catData || []);

      // Load all slides
      const { data: slideData, error: slideError } = await supabaseData
        .from('sales_slides')
        .select('*, sales_categories(name), skus!sku_id(name, sku_code)')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('sort_order');

      if (slideError) throw slideError;
      setSlides(slideData || []);

      // Load SKUs for linking
      const { data: skuData } = await supabaseData
        .from('skus')
        .select('id, name, sku_code, price')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      setSkus(skuData || []);

    } catch (error) {
      showError('Failed to load sales content');
    } finally {
      setLoading(false);
    }
  };

  const filteredSlides = slides.filter(slide => {
    const matchesCategory = !selectedCategory || slide.category_id === selectedCategory;
    const matchesSearch = !searchTerm ||
      slide.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      slide.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      slide.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Category CRUD
  const handleSaveCategory = async (category) => {
    try {
      if (category.id) {
        const { error } = await supabaseData
          .from('sales_categories')
          .update({ name: category.name, description: category.description, icon: category.icon })
          .eq('id', category.id);
        if (error) throw error;
        showSuccess('Category updated');
      } else {
        const { error } = await supabaseData
          .from('sales_categories')
          .insert([{ ...category, company_id: companyId }]);
        if (error) throw error;
        showSuccess('Category created');
      }
      setShowAddCategory(false);
      setEditingCategory(null);
      loadData();
    } catch (error) {
      showError('Failed to save category: ' + error.message);
    }
  };

  const handleDeleteCategory = async (id) => {
    const confirmed = await showConfirm('Delete this category and all its slides?');
    if (!confirmed) return;

    try {
      await supabaseData.from('sales_categories').update({ is_active: false }).eq('id', id);
      showSuccess('Category deleted');
      if (selectedCategory === id) setSelectedCategory(null);
      loadData();
    } catch (error) {
      showError('Failed to delete category');
    }
  };

  // Slide CRUD
  const handleSaveSlide = async (slide) => {
    try {
      const slideData = {
        title: slide.title,
        description: slide.description,
        category_id: slide.category_id,
        image_url: slide.image_url,
        sku_id: slide.sku_id || null,
        tags: slide.tags || []
      };

      if (slide.id) {
        const { error } = await supabaseData
          .from('sales_slides')
          .update(slideData)
          .eq('id', slide.id);
        if (error) throw error;
        showSuccess('Slide updated');
      } else {
        const { error } = await supabaseData
          .from('sales_slides')
          .insert([{ ...slideData, company_id: companyId }]);
        if (error) throw error;
        showSuccess('Slide created');
      }
      setShowAddSlide(false);
      setEditingSlide(null);
      loadData();
    } catch (error) {
      showError('Failed to save slide: ' + error.message);
    }
  };

  const handleDeleteSlide = async (id) => {
    const confirmed = await showConfirm('Delete this slide?');
    if (!confirmed) return;

    try {
      await supabaseData.from('sales_slides').update({ is_active: false }).eq('id', id);
      showSuccess('Slide deleted');
      loadData();
    } catch (error) {
      showError('Failed to delete slide');
    }
  };

  const handleImageUpload = async (file, callback) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${companyId}/sales/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('company-assets')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('company-assets')
        .getPublicUrl(fileName);

      callback(publicUrl);
    } catch (error) {
      showError('Failed to upload image: ' + error.message);
    }
  };

  // Presentation mode navigation
  const nextSlide = () => {
    if (currentSlideIndex < filteredSlides.length - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (viewMode === 'presentation' || showFullscreen) {
        if (e.key === 'ArrowRight' || e.key === ' ') nextSlide();
        if (e.key === 'ArrowLeft') prevSlide();
        if (e.key === 'Escape') {
          setShowFullscreen(false);
          setViewMode('grid');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, showFullscreen, currentSlideIndex, filteredSlides.length]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading sales content...
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h2 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Image size={28} color={colors.accent} />
          Sales Tool
        </h2>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
            <input
              type="text"
              placeholder="Search slides..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                width: '200px'
              }}
            />
          </div>

          {/* View toggle */}
          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'presentation' : 'grid')}
            style={{
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              cursor: 'pointer'
            }}
            title={viewMode === 'grid' ? 'Presentation mode' : 'Grid view'}
          >
            {viewMode === 'grid' ? <Maximize2 size={18} /> : <Grid size={18} />}
          </button>

          {/* Add buttons */}
          <button
            onClick={() => setShowAddCategory(true)}
            style={{
              padding: '0.75rem 1rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <FolderOpen size={18} />
            Add Category
          </button>

          <button
            onClick={() => setShowAddSlide(true)}
            style={{
              padding: '0.75rem 1rem',
              background: colors.accent,
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 'bold'
            }}
          >
            <Plus size={18} />
            Add Slide
          </button>
        </div>
      </div>

      {/* Categories */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        overflowX: 'auto',
        paddingBottom: '0.5rem'
      }}>
        <button
          onClick={() => setSelectedCategory(null)}
          style={{
            padding: '0.5rem 1rem',
            background: !selectedCategory ? colors.accent + '30' : colors.bgInput,
            border: !selectedCategory ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
            borderRadius: '20px',
            color: !selectedCategory ? colors.accent : colors.text,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontWeight: !selectedCategory ? 'bold' : 'normal'
          }}
        >
          All ({slides.length})
        </button>

        {categories.map(cat => {
          const count = slides.filter(s => s.category_id === cat.id).length;
          const isSelected = selectedCategory === cat.id;
          return (
            <div key={cat.id} style={{ position: 'relative', display: 'flex' }}>
              <button
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  padding: '0.5rem 1rem',
                  background: isSelected ? colors.accent + '30' : colors.bgInput,
                  border: isSelected ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                  borderRadius: '20px',
                  color: isSelected ? colors.accent : colors.text,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {cat.icon && <span>{cat.icon}</span>}
                {cat.name} ({count})
              </button>
              <button
                onClick={() => setEditingCategory(cat)}
                style={{
                  position: 'absolute',
                  right: '-8px',
                  top: '-8px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0
                }}
              >
                <Edit2 size={10} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Content */}
      {viewMode === 'grid' ? (
        /* Grid View */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem'
        }}>
          {filteredSlides.length === 0 ? (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '3rem',
              color: colors.textMuted
            }}>
              <Image size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <div>No slides yet. Add your first slide to get started!</div>
              <button
                onClick={() => setShowAddSlide(true)}
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1.5rem',
                  background: colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                <Plus size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Add First Slide
              </button>
            </div>
          ) : (
            filteredSlides.map((slide, index) => (
              <div
                key={slide.id}
                style={{
                  background: colors.bgCard,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onClick={() => {
                  setCurrentSlideIndex(index);
                  setShowFullscreen(true);
                }}
              >
                {/* Image */}
                <div style={{
                  height: '180px',
                  background: colors.bgInput,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {slide.image_url ? (
                    <img
                      src={slide.image_url}
                      alt={slide.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Image size={48} color={colors.textMuted} style={{ opacity: 0.3 }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ padding: '1rem' }}>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: colors.text,
                    marginBottom: '0.5rem'
                  }}>
                    {slide.title}
                  </div>

                  {slide.description && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: colors.textMuted,
                      marginBottom: '0.5rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {slide.description}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {slide.sales_categories && (
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem',
                        background: colors.accent + '20',
                        color: colors.accent,
                        borderRadius: '4px'
                      }}>
                        {slide.sales_categories.name}
                      </span>
                    )}

                    <div style={{ display: 'flex', gap: '0.25rem' }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setEditingSlide(slide)}
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          color: colors.textMuted,
                          cursor: 'pointer'
                        }}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteSlide(slide.id)}
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          color: colors.error,
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* Presentation View */
        <PresentationView
          slides={filteredSlides}
          currentIndex={currentSlideIndex}
          setCurrentIndex={setCurrentSlideIndex}
          colors={colors}
          onClose={() => setViewMode('grid')}
        />
      )}

      {/* Fullscreen Modal */}
      {showFullscreen && filteredSlides[currentSlideIndex] && (
        <FullscreenSlide
          slide={filteredSlides[currentSlideIndex]}
          currentIndex={currentSlideIndex}
          total={filteredSlides.length}
          onNext={nextSlide}
          onPrev={prevSlide}
          onClose={() => setShowFullscreen(false)}
          colors={colors}
        />
      )}

      {/* Add/Edit Category Modal */}
      {(showAddCategory || editingCategory) && (
        <CategoryModal
          category={editingCategory}
          onSave={handleSaveCategory}
          onDelete={editingCategory ? () => handleDeleteCategory(editingCategory.id) : null}
          onClose={() => { setShowAddCategory(false); setEditingCategory(null); }}
          colors={colors}
        />
      )}

      {/* Add/Edit Slide Modal */}
      {(showAddSlide || editingSlide) && (
        <SlideModal
          slide={editingSlide}
          categories={categories}
          skus={skus}
          selectedCategory={selectedCategory}
          onSave={handleSaveSlide}
          onDelete={editingSlide ? () => handleDeleteSlide(editingSlide.id) : null}
          onClose={() => { setShowAddSlide(false); setEditingSlide(null); }}
          onImageUpload={handleImageUpload}
          colors={colors}
        />
      )}
    </div>
  );
}

// Presentation View Component
function PresentationView({ slides, currentIndex, setCurrentIndex, colors, onClose }) {
  const slide = slides[currentIndex];

  if (!slide) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
        No slides to display
      </div>
    );
  }

  return (
    <div style={{
      background: colors.bgCard,
      borderRadius: '16px',
      overflow: 'hidden',
      border: `1px solid ${colors.border}`
    }}>
      {/* Image */}
      <div style={{
        height: '60vh',
        minHeight: '400px',
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}>
        {slide.image_url ? (
          <img
            src={slide.image_url}
            alt={slide.title}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Image size={96} color={colors.textMuted} style={{ opacity: 0.3 }} />
        )}

        {/* Navigation arrows */}
        {currentIndex > 0 && (
          <button
            onClick={() => setCurrentIndex(prev => prev - 1)}
            style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.5)',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ChevronLeft size={28} />
          </button>
        )}

        {currentIndex < slides.length - 1 && (
          <button
            onClick={() => setCurrentIndex(prev => prev + 1)}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.5)',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ChevronRight size={28} />
          </button>
        )}

        {/* Counter */}
        <div style={{
          position: 'absolute',
          bottom: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '0.5rem 1rem',
          background: 'rgba(0,0,0,0.5)',
          borderRadius: '20px',
          color: '#fff',
          fontSize: '0.9rem'
        }}>
          {currentIndex + 1} / {slides.length}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', color: colors.text, fontSize: '1.5rem' }}>
          {slide.title}
        </h3>
        {slide.description && (
          <p style={{ margin: 0, color: colors.textMuted, fontSize: '1.1rem', lineHeight: 1.6 }}>
            {slide.description}
          </p>
        )}

        {slide.skus && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: colors.bgInput,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Package size={18} color={colors.accent} />
            <span style={{ color: colors.text, fontWeight: 'bold' }}>{slide.skus.name}</span>
            {slide.skus.sku_code && (
              <span style={{ color: colors.textMuted }}>({slide.skus.sku_code})</span>
            )}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      <div style={{
        padding: '1rem',
        borderTop: `1px solid ${colors.border}`,
        display: 'flex',
        gap: '0.5rem',
        overflowX: 'auto'
      }}>
        {slides.map((s, idx) => (
          <div
            key={s.id}
            onClick={() => setCurrentIndex(idx)}
            style={{
              width: '60px',
              height: '45px',
              borderRadius: '4px',
              overflow: 'hidden',
              cursor: 'pointer',
              border: idx === currentIndex ? `2px solid ${colors.accent}` : `2px solid transparent`,
              opacity: idx === currentIndex ? 1 : 0.6,
              flexShrink: 0
            }}
          >
            {s.image_url ? (
              <img src={s.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: colors.bgInput }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Fullscreen Slide Modal
function FullscreenSlide({ slide, currentIndex, total, onNext, onPrev, onClose, colors }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.95)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ color: '#fff', fontSize: '0.9rem' }}>
          {currentIndex + 1} / {total}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            padding: '0.5rem'
          }}
        >
          <X size={28} />
        </button>
      </div>

      {/* Image */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: '1rem'
      }}>
        {slide.image_url ? (
          <img
            src={slide.image_url}
            alt={slide.title}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Image size={96} color="#666" />
        )}

        {/* Nav buttons */}
        {currentIndex > 0 && (
          <button
            onClick={onPrev}
            style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            <ChevronLeft size={32} />
          </button>
        )}

        {currentIndex < total - 1 && (
          <button
            onClick={onNext}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            <ChevronRight size={32} />
          </button>
        )}
      </div>

      {/* Caption */}
      <div style={{ padding: '1.5rem', background: 'rgba(0,0,0,0.5)' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#fff', fontSize: '1.25rem' }}>
          {slide.title}
        </h3>
        {slide.description && (
          <p style={{ margin: 0, color: '#ccc', fontSize: '1rem' }}>
            {slide.description}
          </p>
        )}
      </div>
    </div>
  );
}

// Category Modal
function CategoryModal({ category, onSave, onDelete, onClose, colors }) {
  const [name, setName] = useState(category?.name || '');
  const [description, setDescription] = useState(category?.description || '');
  const [icon, setIcon] = useState(category?.icon || '');

  const emojis = ['🔧', '🚪', '⚙️', '🏠', '📸', '✅', '❌', '💡', '🔩', '🛠️', '📋', '💰'];

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: colors.bgCard,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '400px',
        padding: '1.5rem'
      }}>
        <h3 style={{ margin: '0 0 1.5rem 0', color: colors.text }}>
          {category ? 'Edit Category' : 'New Category'}
        </h3>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Before & After"
            style={{
              width: '100%',
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Icon
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {emojis.map(e => (
              <button
                key={e}
                onClick={() => setIcon(e)}
                style={{
                  width: '36px',
                  height: '36px',
                  fontSize: '1.25rem',
                  background: icon === e ? colors.accent + '30' : colors.bgInput,
                  border: icon === e ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={2}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              resize: 'none'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {onDelete && (
            <button
              onClick={onDelete}
              style={{
                padding: '0.75rem 1rem',
                background: colors.error + '20',
                border: 'none',
                borderRadius: '8px',
                color: colors.error,
                cursor: 'pointer'
              }}
            >
              <Trash2 size={18} />
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ id: category?.id, name, description, icon })}
            disabled={!name.trim()}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: name.trim() ? colors.accent : colors.bgInput,
              border: 'none',
              borderRadius: '8px',
              color: name.trim() ? '#fff' : colors.textMuted,
              cursor: name.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// Slide Modal
function SlideModal({ slide, categories, skus, selectedCategory, onSave, onDelete, onClose, onImageUpload, colors }) {
  const [title, setTitle] = useState(slide?.title || '');
  const [description, setDescription] = useState(slide?.description || '');
  const [categoryId, setCategoryId] = useState(slide?.category_id || selectedCategory || '');
  const [imageUrl, setImageUrl] = useState(slide?.image_url || '');
  const [skuId, setSkuId] = useState(slide?.sku_id || '');
  const [tags, setTags] = useState(slide?.tags?.join(', ') || '');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    onImageUpload(file, (url) => {
      setImageUrl(url);
      setUploading(false);
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
      overflowY: 'auto'
    }}>
      <div style={{
        background: colors.bgCard,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '500px',
        padding: '1.5rem',
        margin: 'auto'
      }}>
        <h3 style={{ margin: '0 0 1.5rem 0', color: colors.text }}>
          {slide ? 'Edit Slide' : 'New Slide'}
        </h3>

        {/* Image Upload */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Image
          </label>
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              height: '150px',
              background: colors.bgInput,
              border: `2px dashed ${colors.border}`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden'
            }}
          >
            {uploading ? (
              <div style={{ color: colors.textMuted }}>Uploading...</div>
            ) : imageUrl ? (
              <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ textAlign: 'center', color: colors.textMuted }}>
                <Upload size={32} style={{ marginBottom: '0.5rem' }} />
                <div>Click to upload image</div>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* Title */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Title *
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g., Old Style Springs"
            style={{
              width: '100%',
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text
            }}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Explain what this shows to the customer..."
            rows={3}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              resize: 'none'
            }}
          />
        </div>

        {/* Category */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Category
          </label>
          <select
            value={categoryId}
            onChange={e => setCategoryId(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text
            }}
          >
            <option value="">No category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
            ))}
          </select>
        </div>

        {/* Link to Product */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Link to Product (optional)
          </label>
          <select
            value={skuId}
            onChange={e => setSkuId(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text
            }}
          >
            <option value="">None</option>
            {skus.map(sku => (
              <option key={sku.id} value={sku.id}>
                {sku.name} {sku.sku_code && `(${sku.sku_code})`} - ${sku.price}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
            Tags (comma separated)
          </label>
          <input
            type="text"
            value={tags}
            onChange={e => setTags(e.target.value)}
            placeholder="before, after, comparison"
            style={{
              width: '100%',
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {onDelete && (
            <button
              onClick={onDelete}
              style={{
                padding: '0.75rem 1rem',
                background: colors.error + '20',
                border: 'none',
                borderRadius: '8px',
                color: colors.error,
                cursor: 'pointer'
              }}
            >
              <Trash2 size={18} />
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({
              id: slide?.id,
              title,
              description,
              category_id: categoryId || null,
              image_url: imageUrl,
              sku_id: skuId || null,
              tags: tags.split(',').map(t => t.trim()).filter(Boolean)
            })}
            disabled={!title.trim()}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: title.trim() ? colors.accent : colors.bgInput,
              border: 'none',
              borderRadius: '8px',
              color: title.trim() ? '#fff' : colors.textMuted,
              cursor: title.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
